import React, { useState, useRef, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  setLoanAmmount,
  setTimePeriod,
  setIntrestRate,
  setPerEmi,
  resetPersonalData,
  setTotalAmount,
} from "../redux/calculations/personalLoanSlice";
import {
  updatePersonalLoanChart,
  resetChartData,
} from "../redux/calculations/calculaterSlice";
import CalculatorLayout from "../components/CalcLayout";
import Input from "../components/Input";
import { Button } from "../components/Button";
import { useFormik } from "formik";
import * as Yup from "yup";
import useLocale from "../../src/locales/index";
import { logEvent } from "firebase/analytics";
import { analytics } from "../firebase";
import i18n from "../i18n";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

const PersonalLoanCalc = () => {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const { loanAmount, timePeriod, intrestRate } = useSelector(
    (state) => state.personalLoanCalculator
  );
  const { textData } = useLocale();
  const cAccept = localStorage.getItem("grow-cookies");
  const cookieAccept = JSON.parse(cAccept);
  const [formattedLoanAmount, setFormattedLoanAmount] = useState("");
  const [calcDone, setCalcDone] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);

  const formik = useFormik({
    initialValues: {
      loanAmount: loanAmount,
      timePeriod: timePeriod,
      intrestRate: intrestRate,
    },
    validationSchema: Yup.object({
      loanAmount: Yup.number()
        .min(10000, textData.loanAmountMin)
        .max(20000000, textData.loanAmountMax)
        .required(textData.loanAmountRequired),
      timePeriod: Yup.number()
        .min(1, textData.timePeriodMin)
        .max(30, textData.timePeriodMax)
        .required(textData.timrePeriodRequired),
      intrestRate: Yup.number()
        .min(1, textData.interestRateMin)
        .max(30, textData.interestRateMax)
        .required(textData.interestRateRequired),
    }),
    validateOnChange: true,
    validateOnBlur: true,

    onSubmit: (values) => {
      if (cookieAccept && analytics) {
        logEvent(analytics, "calculate_PERSONALLOAN");
      }
      const principal = parseFloat(values.loanAmount);
      const rate = parseFloat(values.intrestRate) / 1200;
      const time = parseFloat(values.timePeriod) * 12;

      const emi =
        (principal * rate * Math.pow(1 + rate, time)) /
        (Math.pow(1 + rate, time) - 1);
      const totalPayment = emi * time;
      const totalInterest = totalPayment - principal;

      dispatch(setPerEmi(emi));

      dispatch(
        updatePersonalLoanChart({
          loanAmount: principal,
          totalInterest: totalInterest,
          totalPayment: totalPayment,
        })
      );

      saveToHistory({
        tabName: "personalLoanCalc",
        totalInvestment: principal,
        yearlyReturn: totalInterest,
        interestRate: values.intrestRate,
        tenure: values.timePeriod,
        maturityAmount: totalPayment,
        perEmi: emi,
        calculationType: "Personal Loan",
      });
      setShowTable(false);
      setCalcDone(true);
      setIsFormModified(false);
      dispatch(setTotalAmount(totalPayment));
    },
  });
  useEffect(() => {
    return () => {
      dispatch(resetChartData());
      dispatch(resetPersonalData());
    };
  }, [dispatch]);

  const saveToHistory = (calculationData) => {
    try {
      const existingData =
        JSON.parse(localStorage.getItem(textData.storeData)) || [];

      const newHistoryRecord = {
        tabName: calculationData.tabName,
        totalInvestment: calculationData.totalInvestment,
        yearlyReturn: calculationData.yearlyReturn,
        interestRate: calculationData.interestRate,
        tenure: calculationData.tenure,
        perEmi: calculationData.perEmi,
        maturityAmount: calculationData.maturityAmount,
        calculationType: calculationData.calculationType,
        calculationDate: new Date().toISOString(),
      };

      const isDuplicate = existingData.some((recordArray) => {
        if (!recordArray || !recordArray[0]) return false;
        const record = recordArray[0];

        return (
          record.calculationType === newHistoryRecord.calculationType &&
          record.totalInvestment === newHistoryRecord.totalInvestment &&
          record.interestRate === newHistoryRecord.interestRate &&
          record.tenure === newHistoryRecord.tenure &&
          record.perEmi === newHistoryRecord.perEmi &&
          record.maturityAmount === newHistoryRecord.maturityAmount
        );
      });

      if (!isDuplicate) {
        existingData.push([newHistoryRecord]);
        localStorage.setItem(textData.storeData, JSON.stringify(existingData));
      } else {
      }
    } catch (error) {}
  };

  useEffect(() => {
    setFormattedLoanAmount(formatToINR(formik.values.loanAmount));
  }, [formik.values.loanAmount]);

  const sliderTrackRef = useRef(null);
  const interestSliderRef = useRef(null);
  const periodSliderRef = useRef(null);

  const handleTenureSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setTimePeriod(value));
    formik.setFieldValue("timePeriod", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleInterestSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setIntrestRate(value));
    formik.setFieldValue("intrestRate", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleLoanAmountSliderChange = (e) => {
    const value = Number(e.target.value);
    dispatch(setLoanAmmount(value));
    formik.setFieldValue("loanAmount", value);
    setIsFormModified(true);
    setCalcDone(false);
  };

  const formatToINR = (value) => {
    if (value === "" || value === null || value === undefined) return "";
    if (value === 0) return "0";

    const shouldForceIndianFormat = i18n.language === "fa";
    const decimalSeparator = getDecimalSeparator();

    if (isNaN(Number(value))) return "";

    const formattedNumber = Number(value).toLocaleString(
      shouldForceIndianFormat ? "en-IN" : i18n.language || "en-IN"
    );

    // If the language uses comma as decimal separator, format accordingly
    if (decimalSeparator === "," && !shouldForceIndianFormat) {
      // The toLocaleString might already handle this, but ensure consistency
      return formattedNumber;
    }

    return formattedNumber;
  };
  const handleLoanAmountChange = (e) => {
    const rawValue = e.target.value.replace(/[^0-9.,]/g, "");
    const numericValue = parseFloat(
      rawValue
        .replace(/,/g, ".")
        .replace(/\./g, "")
        .replace(/(\d+)(\.\d{2})?/, "$1.$2")
    );

    if (rawValue.length > 11) return;
    if (rawValue.match(/^[.,]+$/)) {
      return;
    }
    if (rawValue === "") {
      formik.setFieldValue("loanAmount", "");
      dispatch(setLoanAmmount(""));
      setFormattedLoanAmount("");
      return;
    }

    formik.setFieldValue("loanAmount", numericValue);
    dispatch(setLoanAmmount(numericValue));
    const formattedValue = new Intl.NumberFormat(i18n.language).format(
      numericValue
    );
    setFormattedLoanAmount(formattedValue);
    setIsFormModified(true);
    setCalcDone(false);
  };
  const getPlaceholderValue = (language) => {
    const baseValue = 10000;
    const formatLang = language === "fa" ? "en-IN" : language;
    return new Intl.NumberFormat(formatLang).format(baseValue);
  };

  const handleTimeChange = (e) => {
    const rawValue = e.target.value;
    if (rawValue.length > 2) return;
    formik.setFieldValue("timePeriod", rawValue);
    if (rawValue === "") return;
    const numericValue = Number(rawValue);
    if (!isNaN(numericValue)) {
      const clamped = Math.max(1, Math.min(30, numericValue));
      dispatch(setTimePeriod(clamped));
    }
    setIsFormModified(true);
    setCalcDone(false);
  };

  const handleInterestRateChange = (e) => {
    const rawValue = e.target.value;
    const decimalSeparator = getDecimalSeparator();

    if (rawValue === "") {
      formik.setFieldValue("intrestRate", rawValue);
      dispatch(setIntrestRate(""));
      return;
    }

    if (rawValue.length > 5) return;

    // Create regex pattern based on decimal separator
    const decimalPattern =
      decimalSeparator === "," ? /^[0-9]*,?[0-9]*$/ : /^[0-9]*\.?[0-9]*$/;

    const isValidInput = decimalPattern.test(rawValue);
    if (!isValidInput) return;

    // Normalize the value for internal storage (always use dot)
    const normalizedValue = normalizeDecimalInput(rawValue, decimalSeparator);

    // Store normalized value internally
    formik.setFieldValue("intrestRate", normalizedValue);
    dispatch(setIntrestRate(normalizedValue));
    setIsFormModified(true);
    setCalcDone(false);
  };
  const isRTL = () => {
    const rtlLanguages = ["ar", "fa", "ur"];
    return rtlLanguages.includes(i18n.language);
  };

  const descriptionContent = (
    <div className="p-4 sm:p-5 w-full bg-[#F6F8FB] rounded-xl">
      <h2 className="text-black text-base sm:text-xl font-medium">
        {textData.personalContent.title}
      </h2>
      <p className="leading-relaxed mt-2  text-sm sm:text-[15px]">
        {textData.personalContent.description}
      </p>
      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {textData.personalContent.howItWorksTitle}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.personalContent.howItWorksDescription}{" "}
      </p>

      <p className="font-medium bg-[#E0E4F0] text-sm sm:text-base p-3 mt-3">
        {textData.personalContent.emiFormula} = [P × R × (1+R)<sup>N</sup>] /
        [(1+R)<sup>N</sup> - 1]
      </p>

      <table className="border-collapse mt-4 w-full">
        <thead>
          <tr className="bg-gray-100">
            <th className="p-3 border border-gray-light w-1/3 text-left text-white bg-[#3498db] font-semibold">
              {textData.personalContent.symbolColumn}
            </th>
            <th className="p-3 border border-gray-light w-2/3 text-left text-white bg-[#3498db] font-semibold">
              {textData.personalContent.descriptionColumn}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              P
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.personalContent.emiSymbolsTable[0].description}
            </td>
          </tr>
          <tr className="bg-transparent">
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              R
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.personalContent.emiSymbolsTable[1].description}
            </td>
          </tr>
          <tr>
            <td className="p-3 border border-gray-light w-1/3 font-bold text-sm sm:text-[15px]">
              N
            </td>
            <td className="p-3 border border-gray-light w-2/3 text-sm sm:text-[15px]">
              {textData.personalContent.emiSymbolsTable[2].description}
            </td>
          </tr>
        </tbody>
      </table>

      <h2 className="text-black text-base sm:text-xl font-medium mt-8">
        {textData.personalContent.benefitsTitle}
      </h2>
      <ul className="list-disc px-5 leading-relaxed mt-3 text-sm sm:text-[15px]">
        <li>{textData.personalContent.benefits[0]}</li>
        <li>{textData.personalContent.benefits[1]}</li>

        <li>{textData.personalContent.benefits[2]}</li>

        <li>{textData.personalContent.benefits[3]}</li>
        <li>{textData.personalContent.benefits[4]}</li>
      </ul>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {textData.personalContent.exampleCalc}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.personalContent.exampleDDec}{" "}
      </p>

      <h2 className="text-black text-base sm:text-xl font-medium mt-5">
        {textData.personalContent.scedule}
      </h2>
      <p className="leading-relaxed mt-2 text-sm sm:text-[15px]">
        {textData.personalContent.sceduleDec}{" "}
      </p>
    </div>
  );
  const timeLabel = t("yearShort");

  const getDecimalSeparator = () => {
    // Languages that typically use comma as decimal separator
    const commaDecimalLanguages = [
      "de",
      "fr",
      "es",
      "it",
      "nl",
      "pt",
      "ru",
      "tr",
      "pl",
      "cs",
      "sk",
      "hu",
      "ro",
      "bg",
      "hr",
      "sl",
      "et",
      "lv",
      "lt",
    ];

    // Check if current language uses comma as decimal separator
    return commaDecimalLanguages.includes(i18n.language) ? "," : ".";
  };

  // Add this helper function to normalize input for validation and storage
  const normalizeDecimalInput = (value, decimalSeparator) => {
    if (decimalSeparator === ",") {
      // Replace comma with dot for internal processing
      return value.replace(",", ".");
    }
    return value;
  };

  // Add this helper function to format display value
  const formatDecimalDisplay = (value, decimalSeparator) => {
    if (decimalSeparator === "," && typeof value === "string") {
      // Replace dot with comma for display
      return value.replace(".", ",");
    }
    return value;
  };

  return (
    <>
      <Helmet>
        <title>Online EMI Calculator for Personal Loans | GrowLifeFund</title>
        <meta
          name="description"
          content="GrowLifeFund Online EMI Calculator for Personal Loans lets you calculate EMIs instantly, plan smart repayments, and manage your finances easily.Try Now!"
        />
        <link
          rel="canonical"
          href="https://growlifefund.com/calculator/personal-loan-calculator"
        />

        <meta
          property="og:title"
          content="Online EMI Calculator for Personal Loans | GrowLifeFund"
        />
        <meta
          property="og:description"
          content="GrowLifeFund Online EMI Calculator for Personal Loans lets you calculate EMIs instantly, plan smart repayments, and manage your finances easily.Try Now!"
        />
        <meta
          property="og:url"
          content="https://growlifefund.com/calculator/personal-loan-calculator"
        />
        <meta
          property="og:image:alt"
          content="Online EMI Calculator for Loans - GrowLifeFund"
        />

        <meta property="og:site_name" content="growlifefund" />
        <meta property="og:type" content="website" />
        <meta property="og:locale" content="en" />

        <meta name="robots" content="index, follow" />
      </Helmet>

      <CalculatorLayout
        title={
          <>
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.your}{" "}
            </span>
            {textData.personalLoan}{" "}
            <span className="text-black lg:text-xl sm:text-lg text-lg">
              {textData.emis}
            </span>
          </>
        }
        description={textData.personalTital}
        descriptionContent={descriptionContent}
        setShowTable={setShowTable}
        showTable={showTable}
        calcDone={calcDone}
        setCalcDone={setCalcDone}
        isFormModified={isFormModified}
        setIsFormModified={setIsFormModified}
      >
        <div className="">
          <form onSubmit={formik.handleSubmit} noValidate className="flex flex-col gap-6">
            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.loanAmount}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={sliderTrackRef}>
                  
                  <input
                    type="range"
                    min={10000}
                    max={20000000}
                    step={1000}
                    value={formik.values.loanAmount}
                    onChange={handleLoanAmountSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        ((formik.values.loanAmount - 10000) /
                          (20000000 - 10000)) *
                        100
                      }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="loanAmount"
                    leftIcon={textData.currency}
                    value={formattedLoanAmount}
                    onChange={handleLoanAmountChange}
                    onBlur={formik.handleBlur}
                    type="text"
                    dir="ltr"
                    placeholder={getPlaceholderValue(i18n.language)}
                    borderColor={
                      formik.touched.loanAmount && formik.errors.loanAmount
                        ? "border-danger"
                        : "border-gray-light"
                    }
                    className=" sm:h-[36px] h-[32px]} flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"
                    style={{ direction: isRTL() ? "rtl" : "ltr" }}
                  />
                  {formik.touched.loanAmount && formik.errors.loanAmount && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.loanAmount}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.rateOfInterest}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={interestSliderRef}>
                  
                  <input
                    type="range"
                    min={5}
                    max={30}
                    step={0.1}
                    value={parseFloat(formik.values.intrestRate)}
                    onChange={handleInterestSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        ((formik.values.intrestRate - 5) / (30 - 5)) * 100
                      }%`,
                    }}
                  />
                </div>
                <div className="flex items-center flex-col">
                  <Input
                    name="intrestRate"
                    leftIcon="%"
                    type="text"
                    placeholder={
                      getDecimalSeparator() === "," ? "12,5" : "12.5"
                    }
                    value={formatDecimalDisplay(
                      formik.values.intrestRate,
                      getDecimalSeparator()
                    )}
                    onChange={handleInterestRateChange}
                    onBlur={formik.handleBlur}
                    borderColor={
                      formik.touched.intrestRate && formik.errors.intrestRate
                        ? "border-danger"
                        : "border-gray-light"
                    }
                    className=" sm:h-[36px] h-[32px]} flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"
                  />
                  {formik.touched.intrestRate && formik.errors.intrestRate && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.intrestRate}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div>
              <div className="flex justify-between items-center mb-2 gap-4">
                <label className="text-start font-normal md:text-sm text-black sm:text-sm text-sm">
                  {textData.timePeriod}
                </label>
              </div>
              <div className="flex justify-between gap-5 items-center">
                <div className="relative   w-full " ref={periodSliderRef}>
                  
                  <input
                    type="range"
                    min={1}
                    max={30}
                    step={1}
                    value={formik.values.timePeriod}
                    onChange={handleTenureSliderChange}
                    className="flex-1 min-w-0 max-w-[314px]"
                    style={{
                      width: "100%",
                      appearance: "none",
                      background: "transparent",
                      ...(isRTL() && {
                        direction: "ltr",
                      }),
                      "--val": `${
                        ((formik.values.timePeriod - 1) / (30 - 1)) * 100
                      }%`,
                    }}
                  />
                </div>{" "}
                <div className="flex items-center flex-col">
                  <Input
                    name="timePeriod"
                    leftIcon={timeLabel}
                    value={formik.values.timePeriod}
                    onChange={handleTimeChange}
                    onBlur={formik.handleBlur}
                    type="number"
                    placeholder={"5"}
                    borderColor={
                      formik.touched.timePeriod && formik.errors.timePeriod
                        ? "border-danger"
                        : "border-gray-light"
                    }
                    className=" sm:h-[36px] h-[32px]} flex items-center py-[7.5px] rounded-[6px] focus:outline-none mt-0 px-[8px] sm:px-[12px] sm:w-[170px] w-[140px]"
                  />
                  {formik.touched.timePeriod && formik.errors.timePeriod && (
                    <div className="text-danger text-[12px] text-center sm:text-[13px] mt-1">
                      {formik.errors.timePeriod}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="mt-[10px]">
              <Button type="submit" className="w-full">
                {textData.calculate}
              </Button>
            </div>
          </form>
        </div>
      </CalculatorLayout>
    </>
  );
};

export default PersonalLoanCalc;
